import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { customerAPI } from '@matix/api-client';
import { CUSTOMER_MEASUREMENT_FIELDS, type CustomerMeasurementValues } from '@matix/types';
import { useLang } from '../../lib/language';
import { toast } from '../../store/toast';

/**
 * A customer's measurements (POS Settings → "Customer measurements",
 * POS_CustomerMeasurements; migration 078). One set per customer, cash or
 * credit, in centimetres. Opened from the cart's customer card and from the
 * customer form; the field list is CUSTOMER_MEASUREMENT_FIELDS in @matix/types.
 */
export function CustomerMeasurementsModal({
  customerType, customerId, customerName, onClose,
}: {
  customerType: 'cash' | 'credit';
  customerId: number;
  customerName: string | null;
  onClose: () => void;
}) {
  const { t, lang } = useLang();
  const [values, setValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    customerAPI
      .getMeasurements(customerType, customerId)
      .then((m) => {
        if (cancelled) return;
        setAvailable(m.available);
        setUpdatedAt(m.updated_at);
        setNotes(m.notes ?? '');
        setValues(Object.fromEntries(Object.entries(m.values).map(([k, v]) => [k, v == null ? '' : String(v)])));
      })
      .catch(() => {
        if (!cancelled) toast(t({ en: 'Could not load the measurements', ar: 'تعذر تحميل المقاسات' }), 'error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // t is a fresh function per render; the customer is what decides the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerType, customerId]);

  async function save() {
    const out: CustomerMeasurementValues = {};
    for (const f of CUSTOMER_MEASUREMENT_FIELDS) {
      const raw = (values[f.key] ?? '').trim();
      if (raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > 999.99) {
        toast(t({ en: `Check "${f.en}"`, ar: `تحقق من "${f.ar}"` }), 'warning');
        return;
      }
      out[f.key] = Math.round(n * 100) / 100;
    }
    setSaving(true);
    try {
      await customerAPI.saveMeasurements(customerType, customerId, out, notes.trim() || null);
      toast(t({ en: 'Measurements saved', ar: 'تم حفظ المقاسات' }), 'success');
      onClose();
    } catch {
      toast(t({ en: 'Could not save the measurements', ar: 'تعذر حفظ المقاسات' }), 'error');
    } finally {
      setSaving(false);
    }
  }

  // Portalled to <body>: it opens from inside other dialogs (the customer
  // picker, the customer form), where a transformed ancestor would otherwise
  // trap this fixed overlay inside that dialog's box.
  return createPortal(
    <div className="modal-overlay show" style={{ zIndex: 200 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
        style={{ maxWidth: 620, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <div>
            <div className="modal-title">{t({ en: 'Measurements', ar: 'المقاسات' })}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {customerName ?? `#${customerId}`} · {t({ en: 'in cm', ar: 'بالسنتيمتر' })}
              {updatedAt && ` · ${t({ en: 'updated', ar: 'آخر تعديل' })} ${new Date(updatedAt).toLocaleDateString()}`}
            </div>
          </div>
          <button className="btn-close" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
              {t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}
            </div>
          ) : !available ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--danger)' }}>
              {t({
                en: 'Measurements are not set up on this database yet — ask your administrator.',
                ar: 'المقاسات غير مُعدّة في قاعدة البيانات بعد — راجع مسؤول النظام.',
              })}
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
                {CUSTOMER_MEASUREMENT_FIELDS.map((f) => (
                  <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
                      {lang === 'ar' ? f.ar : f.en}
                    </span>
                    {(f.hintEn || f.hintAr) && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -2 }}>
                        {lang === 'ar' ? f.hintAr : f.hintEn}
                      </span>
                    )}
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={999.99}
                      step="any"
                      className="kitchen-note"
                      // Bottom of the cell: a field without a hint line keeps
                      // its box level with a neighbour that has one.
                      style={{ minHeight: 0, padding: '9px 10px', textAlign: 'center', marginTop: 'auto' }}
                      value={values[f.key] ?? ''}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      placeholder="—"
                    />
                  </label>
                ))}
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 14 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>{t({ en: 'Notes', ar: 'ملاحظات' })}</span>
                <input
                  type="text"
                  className="kitchen-note"
                  style={{ minHeight: 0, padding: '9px 10px' }}
                  maxLength={500}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', padding: '12px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t({ en: 'Close', ar: 'إغلاق' })}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={loading || saving || !available}
            onClick={() => void save()}
          >
            {t({ en: 'Save', ar: 'حفظ' })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
